<!--
Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
Unauthorized use, copying, distribution, displaying, or public performance
of this file, via any medium, is strictly prohibited. All information
contained herein is proprietary and confidential and may not be shared
with any third party without the express written consent of Reciprocity, Inc.
-->

<template>
  <div v-if="serverData" class="wrapper" style="min-height: calc(100vh - 3rem)">
    <h1 class="title">
      <b-button class="back" variant="link" @click="goBack">
        <i class="fa fa-arrow-left" />
        Back </b-button
      >Send questionnaire for:
      <span class="font-weight-bold">{{ serverData.template.title }}</span>
      <help-link />
    </h1>
    <div class="frame">
      <wizard-progress :steps="stepsLabels" :current-step="currentStep" />
      <div class="content">
        <div v-if="loading" class="loader-wrapper">
          <div class="loader" />
          Sending your emails, please hold.
        </div>
        <recipient-details
          v-else-if="currentStep === 0"
          ref="recipientDetails"
          v-model="formData"
          :attempted="stepAttempt[0]"
          @update-validity="setValidity(0, $event)"
          @update-email-data="updateEmailMessage($event)"
        />
        <review
          v-else-if="currentStep === 1"
          :survey="serverData.template"
          :form-data="formData"
          :email-preview-url-base="serverData.previewEmailUrl"
        />
      </div>
      <div class="button-wrapper">
        <b-button :disabled="currentStep === 0" variant="outline-primary" @click="moveStep('back')">
          Previous
        </b-button>
        <b-button :disabled="loading || currentStep === stepsCount" variant="primary" @click="moveStep('forward')">
          {{ currentStep >= stepsCount - 1 ? 'Submit' : 'Next' }}
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { cloneDeep } from 'lodash';
import { mapGetters } from 'vuex';
import { BButton } from 'bootstrap-vue';

import HelpLink from '@/components/help_link/HelpLink';

import RecipientDetails from './pages/2_recipient_details/1-ResendRecipientDetails.vue';
import Review from './pages/3-Review.vue';
import WizardProgress from './components/WizardProgress.vue';
import surveyPageMethods from './surveyPageMethods';

export default Vue.extend({
  name: 'Wizard',
  components: {
    BButton,
    RecipientDetails,
    Review,
    WizardProgress,
    HelpLink,
  },
  data() {
    return {
      currentStep: 0,
      template: {},
      selectedSurveyId: null,
      stepValidity: [false, true],
      stepAttempt: [false, false],
      serverData: null,
      loading: true,
      formData: {
        recipients: [
          {
            name: '',
            email: '',
          },
        ],
        message: '',
        greeting: '',
        salutations: '',
        contactEmail: '',
        title: '',
        internal: false,
      },
      stepsLabels: ['Recipient details', 'Review'],
      redirectUrl: '',
    };
  },
  computed: {
    stepsCount() {
      return this.stepsLabels.length;
    },
    ...mapGetters(['routes']),
  },
  async created() {
    this.redirectUrl = document.referrer;
    this.setPageTitle({ title: 'Resend Questionnaire', visible: false });
    try {
      this.serverData = await this.request({
        route: 'getSurveyResendWizardData',
        params: {
          public_id: this.$route.params.publicId,
        },
        defaultErrorHandler: false,
      });
      this.prefillFormData();
    } catch (error) {
      this.showAlert({
        type: 'error',
        message: (error.response && error.response.data && error.response.data.message) || 'Something went wrong.',
      });
      this.redirect(this.redirectUrl);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    ...surveyPageMethods,
    setDefaultValues(formData) {
      if (!formData.greeting || formData.greeting === '') {
        formData.greeting = 'Hi John Doe';
      }
      formData.recipients.forEach(recipient => {
        if (recipient.name === '') {
          recipient.name = null;
        }
      });
    },
    async submit() {
      const formData = cloneDeep(this.formData);
      this.setDefaultValues(formData);

      const postData = {
        ...formData,
        surveyIds: [this.selectedSurveyId],
      };

      try {
        this.loading = true;
        await this.request({
          route: 'surveyResponseUpdate',
          method: 'put',
          data: postData,
          defaultErrorHandler: false,
        });

        const url = this.routes.surveyResend.replace('__PUBLIC_ID__', this.$route.params.publicId);

        const response = await this.request({
          url,
          method: 'put',
          defaultErrorHandler: false,
        });

        this.showAlert({
          type: 'success',
          message: 'Questionnaire resent successfully.',
        });
        setTimeout(() => {
          this.redirect(this.redirectUrl || response.redirectUrl);
        }, 1000);
      } catch (error) {
        this.showAlert({
          type: (error.response && error.response.data && error.response.data.type) || 'error',
          message: (error.response && error.response.data && error.response.data.message) || 'Something went wrong.',
        });
        this.moveStep('back');
      } finally {
        this.loading = false;
      }
    },
    prefillFormData() {
      this.formData.message = this.serverData.message;
      this.formData.greeting = this.serverData.greeting;
      this.formData.salutations = this.serverData.salutations;
      this.formData.contactEmail = this.serverData.contactEmail;
      this.formData.title = this.serverData.title;
      this.formData.recipients = [
        {
          name: this.serverData.surveyTakerName,
          email: this.serverData.surveyTakerEmail,
        },
      ];
      this.formData.notifyOnResponse = this.serverData.notifyOnResponse;
      this.selectedSurvey = this.serverData.template;
      this.selectedSurveyId = this.serverData.id;
    },
  },
});
</script>

<style lang="scss" scoped>
@import './surveyPage.scss';
</style>
